import { forwardRef } from 'react';
import { render, type AccountMode, type MessageContext } from '@sl/core';
import { Icon } from '../../ui/Icon';
import { cx } from '../../ui/cx';
import { testId } from '../testId';
import { MODE_CHIP_HINT, MODE_FAMILY, MODE_ICON, modeMessage } from './copy';
import { useShell } from './shellContext';
import styles from './AppShell.module.css';

// The mode chip — REQ-006, DESIGN_SYSTEM §12.2 (`.lock-chip`). Exactly one is visible on every signed-in
// screen; the label is the catalogue's, spelled exactly (`mode.*`), and the state is readable without colour:
// an icon as well as the dot (AC3). The chip is a BUTTON: clicking it opens the mode menu (AC2).
//
// It is also repeated by every surface that covers the shell (AC5) — the review modal's `modeChip` slot takes
// `<ModeChipLabel testID="review.mode-chip" />`, which is this chip without the trigger.

export interface ModeChipLabelProps {
  mode: AccountMode;
  messages: MessageContext;
  /** Default `shell.mode-chip`; the covering surfaces pass their own (`review.mode-chip`). */
  testID?: string;
  className?: string;
  /**
   * `false` when the element AROUND this face already carries the id — the trigger below. **T-076-W4**: SC-6
   * walks `shell.mode-chip` on every screen and QA's journeys CLICK it, so the id belongs on the control, not
   * on a `<span>` inside it. Exactly one element may carry the id (TEST_PLAN §1.4), so the face gives it up
   * rather than the two of them both claiming it.
   */
  carriesTestID?: boolean;
}

/** The chip's face: dot · icon · exact label. Used on its own where there is nothing to open. */
export function ModeChipLabel({
  mode,
  messages,
  testID,
  className,
  carriesTestID = true,
}: ModeChipLabelProps) {
  return (
    <span
      className={cx(styles.chip, className)}
      data-mode={mode}
      data-mode-family={MODE_FAMILY[mode]}
      data-testid={carriesTestID ? (testID ?? testId('shell', 'mode-chip')) : undefined}
    >
      <span className={styles.chipDot} aria-hidden="true" />
      {/* REQ-006 AC3 is "icon + label" and SC-6 enforces it; `copy.ts` records why design QA item 7 is
          still open rather than applied. */}
      <Icon name={MODE_ICON[mode]} size={14} />
      <span className={styles.chipText}>{render(modeMessage(mode), messages)}</span>
    </span>
  );
}

/**
 * The chip AS REPEATED by a surface that covers the shell — REQ-006 AC5, **BUG-009**. A popover is anchored
 * and leaves the top bar's chip on screen; a sheet, a dialog and the command palette are the whole screen at
 * 360 px, so each says the mode itself, in its own header, exactly as the phone app's `Sheet` already does
 * (`period.sheet.mode-chip`, `shell.mode-menu.mode-chip`, `list.filter-sheet.mode-chip`).
 *
 * It reads the account from the shell context, so a caller passes nothing but the id its surface owns — and
 * because each surface has its OWN id, `shell.mode-chip` is still on exactly one element (TEST_PLAN §1.4).
 */
export function SurfaceModeChip({ testID, className }: { testID: string; className?: string }) {
  const { account, messages } = useShell();
  return (
    <ModeChipLabel mode={account.mode} messages={messages} testID={testID} className={className} />
  );
}

export interface ModeChipProps extends ModeChipLabelProps {
  open: boolean;
  onToggle: () => void;
  /** Id of the panel this chip controls, for `aria-controls`. */
  menuId: string;
  triggerLabel: string;
}

/**
 * The chip as the mode menu's trigger (REQ-006 AC2). Focus returns here when the menu closes (K6).
 *
 * **T-076-W4**: the BUTTON carries `shell.mode-chip` — QA's SC-6 reads the id's text, its icon and whether
 * anything covers it, and the journeys click it. `data-mode` / `data-mode-family` ride along with it (the
 * parity crawl reads `data-mode` off this id) while the face keeps its own copy, which is what the stylesheet
 * selects on. The face's id is suppressed so the id is on exactly one element.
 */
export const ModeChip = forwardRef<HTMLButtonElement, ModeChipProps>(function ModeChip(
  { mode, messages, open, onToggle, menuId, triggerLabel, testID, className },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      className={cx(styles.chipButton, className)}
      title={MODE_CHIP_HINT[mode]}
      aria-label={`${render(modeMessage(mode), messages)} — ${triggerLabel}`}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-controls={open ? menuId : undefined}
      data-mode={mode}
      data-mode-family={MODE_FAMILY[mode]}
      data-testid={testID ?? testId('shell', 'mode-chip')}
      onClick={onToggle}
    >
      <ModeChipLabel mode={mode} messages={messages} carriesTestID={false} />
    </button>
  );
});
